// repo/primary-care-connect/frontend/src/pages/Portal/AcceptInvite/AcceptInvitePage.tsx

import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";

type AcceptInviteResponse = {
  ok?: boolean;
  role?: "FamilyDoctor" | "Specialist" | string;
  clinicId?: number;
  userId?: number;
  redirectTo?: string;
  needsSpecialistProfile?: boolean;
  [k: string]: any;
};

type ClinicMoveConfirmation = {
  currentClinicId: number;
  currentClinicName: string;
  newClinicId: number;
  newClinicName: string;
};

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

function asString(v: any): string {
  if (v === null || v === undefined) return "";
  return String(v).trim();
}

function asInt(v: any): number | null {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : null;
}

function parseClinicMoveConfirmation(body: any): ClinicMoveConfirmation | null {
  if (!body || body.requiresClinicMoveConfirmation !== true) return null;
  if (asString(body.code) !== "CLINIC_MOVE_CONFIRMATION_REQUIRED") return null;

  const currentClinicId = asInt(body.currentClinicId);
  const newClinicId = asInt(body.newClinicId);
  if (currentClinicId == null || newClinicId == null) return null;

  return {
    currentClinicId,
    currentClinicName:
      asString(body.currentClinicName) || "your current clinic",
    newClinicId,
    newClinicName: asString(body.newClinicName) || "the inviting clinic",
  };
}

async function postAcceptInvite(
  token: string,
  payload?: { confirmClinicMove: true; confirmedFromClinicId: number }
): Promise<AcceptInviteResponse> {
  // Must match function.json:
  // POST /api/portal/provider-invites/accept?token=...
  const res = await fetch(
    `/api/portal/provider-invites/accept?token=${encodeURIComponent(token)}`,
    {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: payload ? JSON.stringify(payload) : undefined,
    }
  );

  const text = await res.text();
  const body = text
    ? (() => {
        try {
          return JSON.parse(text);
        } catch {
          return { raw: text };
        }
      })()
    : null;

  if (!res.ok) {
    const msg =
      asString((body as any)?.error) ||
      `${res.status} ${res.statusText}` ||
      "Request failed";
    const err: any = new Error(msg);
    err.status = res.status;
    err.body = body;
    throw err;
  }

  return (body || {}) as AcceptInviteResponse;
}

export default function AcceptInvitePage() {
  const q = useQuery();
  const navigate = useNavigate();

  const token = asString(q.get("token"));

  const [loading, setLoading] = useState(true);
  const [confirmingMove, setConfirmingMove] = useState(false);
  const [statusMsg, setStatusMsg] = useState<string>(
    "Processing your invitation…"
  );
  const [error, setError] = useState<string | null>(null);
  const [httpStatus, setHttpStatus] = useState<number | null>(null);
  const [clinicMove, setClinicMove] = useState<ClinicMoveConfirmation | null>(
    null
  );

  useEffect(() => {
    let mounted = true;
    let redirectedToLogin = false;

    async function run() {
      try {
        setLoading(true);
        setError(null);
        setHttpStatus(null);
        setClinicMove(null);

        if (!token) {
          setStatusMsg("Missing invitation token.");
          setError(
            "This invitation link is incomplete. Please request a new invite email."
          );
          return;
        }

        setStatusMsg("Validating your invitation…");

        const resp = await postAcceptInvite(token);

        if (!mounted) return;

        if (resp?.ok) {
          const redirectTo = asString(resp.redirectTo) || "/portal";
          setStatusMsg("Invitation accepted. Redirecting…");

          setTimeout(() => {
            navigate(redirectTo, { replace: true });
          }, 600);

          return;
        }

        setStatusMsg("We couldn’t accept this invitation.");
        setError("Unexpected response while accepting invite.");
      } catch (e: any) {
        if (!mounted) return;

        const code = Number(e?.status || 0) || null;
        setHttpStatus(code);

        // 401 from backend means not authenticated
        if (code === 401) {
          redirectedToLogin = true;

          const returnTo = `/portal/accept-invite?token=${encodeURIComponent(
            token
          )}`;

          navigate(`/portal/login?returnTo=${encodeURIComponent(returnTo)}`, {
            replace: true,
          });

          // Important: stop here so we don’t show an error state / stop loading flicker.
          return;
        }

        const move = parseClinicMoveConfirmation(e?.body);
        if (code === 409 && move) {
          setClinicMove(move);
          setStatusMsg("You're currently associated with another clinic");
          setError(null);
          setHttpStatus(null);
          return;
        }

        setStatusMsg("We couldn’t accept this invitation.");
        setError(e?.message || "Failed to accept invite.");
      } finally {
        // If we navigated away to login, don't update the local UI state.
        if (!mounted) return;
        if (redirectedToLogin) return;

        setLoading(false);
      }
    }

    run();

    return () => {
      mounted = false;
    };
  }, [token, navigate]);

  async function onConfirmClinicMove() {
    if (!token || !clinicMove || confirmingMove) return;

    try {
      setConfirmingMove(true);
      setError(null);
      setHttpStatus(null);
      setStatusMsg("Confirming clinic move…");

      const resp = await postAcceptInvite(token, {
        confirmClinicMove: true,
        confirmedFromClinicId: clinicMove.currentClinicId,
      });

      if (resp?.ok) {
        const redirectTo = asString(resp.redirectTo) || "/portal";
        setClinicMove(null);
        setStatusMsg("Invitation accepted. Redirecting…");

        setTimeout(() => {
          navigate(redirectTo, { replace: true });
        }, 600);

        return;
      }

      setStatusMsg("We couldn’t accept this invitation.");
      setError("Unexpected response while accepting invite.");
    } catch (e: any) {
      const code = Number(e?.status || 0) || null;
      const move = parseClinicMoveConfirmation(e?.body);

      if (code === 409 && move) {
        setClinicMove(move);
        setStatusMsg("You're currently associated with another clinic");
        setError(null);
        setHttpStatus(null);
        return;
      }

      setHttpStatus(code);
      setClinicMove(null);
      setStatusMsg("We couldn’t accept this invitation.");
      setError(e?.message || "Failed to accept invite.");
    } finally {
      setConfirmingMove(false);
    }
  }

  function onCancelClinicMove() {
    navigate("/portal", { replace: true });
  }

  return (
    <div style={{ maxWidth: 720, margin: "0 auto", padding: 24 }}>
      <h1 style={{ margin: "0 0 10px 0" }}>Accept Invitation</h1>
      <p style={{ margin: "0 0 16px 0", color: "#6b7a94" }}>{statusMsg}</p>

      {loading ? (
        <div
          style={{
            background: "#fff",
            border: "1px solid rgba(15, 80, 135, 0.12)",
            borderRadius: 14,
            padding: 16,
          }}
        >
          Loading…
        </div>
      ) : null}

      {!loading && clinicMove ? (
        <div
          style={{
            background: "#fff",
            border: "1px solid rgba(180, 120, 20, 0.35)",
            borderRadius: 14,
            padding: 16,
          }}
        >
          <div style={{ fontWeight: 700, color: "#92400e", marginBottom: 10 }}>
            You're currently associated with another clinic
          </div>

          <div style={{ color: "#334e68", lineHeight: 1.55 }}>
            <p style={{ margin: "0 0 12px 0" }}>
              You are currently associated with{" "}
              <strong>{clinicMove.currentClinicName}</strong>.
            </p>
            <p style={{ margin: "0 0 12px 0" }}>
              Accepting this invitation will deactivate your association with{" "}
              <strong>{clinicMove.currentClinicName}</strong> and activate your
              association with <strong>{clinicMove.newClinicName}</strong>.
            </p>
            <p style={{ margin: 0 }}>
              You will lose access to {clinicMove.currentClinicName}'s patient
              request lists and clinic-specific workflow. Historical patient
              requests and records will remain with{" "}
              {clinicMove.currentClinicName}.
            </p>
          </div>

          <div
            style={{
              marginTop: 16,
              display: "flex",
              gap: 12,
              flexWrap: "wrap",
            }}
          >
            <button
              type="button"
              onClick={onConfirmClinicMove}
              disabled={confirmingMove}
              style={{
                background: confirmingMove ? "#94a3b8" : "#1669b3",
                color: "#fff",
                padding: "10px 14px",
                borderRadius: 10,
                fontWeight: 600,
                border: "none",
                cursor: confirmingMove ? "not-allowed" : "pointer",
              }}
            >
              {confirmingMove
                ? "Moving…"
                : `Accept and Move to ${clinicMove.newClinicName}`}
            </button>

            <button
              type="button"
              onClick={onCancelClinicMove}
              disabled={confirmingMove}
              style={{
                background: "transparent",
                color: "#1669b3",
                padding: "10px 14px",
                borderRadius: 10,
                fontWeight: 600,
                border: "1px solid rgba(22, 105, 179, 0.35)",
                cursor: confirmingMove ? "not-allowed" : "pointer",
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      ) : null}

      {error ? (
        <div
          style={{
            marginTop: 14,
            background: "#fff",
            border: "1px solid rgba(180, 35, 24, 0.25)",
            borderRadius: 14,
            padding: 16,
          }}
        >
          <div style={{ fontWeight: 700, color: "#b42318", marginBottom: 6 }}>
            {error}
          </div>

          {httpStatus ? (
            <div style={{ color: "#6b7a94", marginBottom: 10 }}>
              {httpStatus}
            </div>
          ) : null}

          <div style={{ color: "#334e68" }}>
            Make sure you are signed in using the{" "}
            <strong>same email address</strong> that received the invitation.
          </div>

          <div
            style={{
              marginTop: 12,
              display: "flex",
              gap: 12,
              flexWrap: "wrap",
            }}
          >
            <Link
              to="/portal/login"
              style={{
                textDecoration: "none",
                background: "#1669b3",
                color: "#fff",
                padding: "10px 14px",
                borderRadius: 10,
                fontWeight: 600,
              }}
            >
              Go to Portal Login
            </Link>

            <Link
              to="/portal"
              style={{
                textDecoration: "none",
                background: "transparent",
                color: "#1669b3",
                padding: "10px 14px",
                borderRadius: 10,
                fontWeight: 600,
                border: "1px solid rgba(22, 105, 179, 0.35)",
              }}
            >
              Go to Portal Home
            </Link>
          </div>
        </div>
      ) : null}
    </div>
  );
}
